import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Button from '../common/Button';
import { ROLES } from '../../utils/constants';
import { authenticatedFetch } from '../../api/apiClient';
import { fetchWithAuth, getSites, getTenantMemberships, updateTenantMembershipAdmin } from '../../api/backendApi';
import { ALGO_DEVICE_SUPPORT_HELP, ALGO_DEVICE_SUPPORT_LABEL, canAssignAlgoDeviceSupport } from '../../pages/Admin/algoDeviceSupport';
import { API_ROUTES } from '../../shared/apiRoutes/apiRoutes';
import { useTenant } from '../../libs/contexts/TenantContext';
import { getFirestore, collection, query, onSnapshot } from 'firebase/firestore';
import { readSupportedTenantSession } from '../../pages/Admin/supportedTenantsSession';
import { useAuth } from '../../contexts/AuthContext';
import { canAssignPricing } from '../../pages/Admin/pricingAccess';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { formatInviteDate, formatInviteStatus } from '../../utils/inviteStatus';
import SiteEditorDialog from './sites/SiteEditorDialog';

function tenantDisplayName(tenants, tenantId, fallbackName) {
  if (fallbackName) return fallbackName;
  const id = String(tenantId || '');
  if (!id) return '—';
  const found = (tenants || []).find((tenant) => (
    tenant.id === id || tenant.tenantId === id || tenant.tenant_id === id
  ));
  return found?.name || id;
}

const UserFormModal = ({ user, isOpen, onClose, onSave, isNew = false, editorRole, defaultTenantId, defaultTenantName }) => {
  // Define initial state with all possible fields
  const initialState = {
    uid: '',
    email: '',
    displayName: '',
    photoURL: '',
    authProvider: '',
    role: ROLES.TENANT_ADMIN,
    tenantId: defaultTenantId || 'demo',
    permissions: {
      canConfigure: false,
      canTriggerEvents: false,
      receivesNotifications: false,
      phoneEnabled: false,
      remindersEnabled: false,
      emailRemEnabled: false,
    },
    requiredAgreements: {
      nda: false,
      beta: false,
    },
    claims: {
      role: ROLES.TENANT_ADMIN,
    },
    customClaims: {
      role: ROLES.TENANT_ADMIN,
    },
    createdAt: null,
    updatedAt: null,
    lastLogin: null,
  };

  const [formData, setFormData] = useState({ ...initialState, ...user });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteStatus, setInviteStatus] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteEditorOpen, setSiteEditorOpen] = useState(false);
  const [tenantPstn, setTenantPstn] = useState({ loading: false, enabled: false, callerIdFrom: '' });
  const [algoDeviceSupport, setAlgoDeviceSupport] = useState(false);
  const { role: sessionRole, currentUser, userClaims, userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const assignerRole = editorRole || sessionRole || userData?.role || userClaims?.role;
  const canAssignAlgo = canAssignAlgoDeviceSupport({
    role: sessionRole || userData?.role || userClaims?.role,
    dealerActive: Boolean(dealerAdmin?.active),
  });

  const effectiveEditorRole = editorRole || 'admin';

  const roleOptions = React.useMemo(() => {
    // Canonical role set (Phase 1A normalized)
    const base = [
      { value: ROLES.TENANT_ADMIN, label: 'Tenant Admin' },
      { value: 'site_admin', label: 'Site Admin' },
      { value: 'operator', label: 'Operator' },
      { value: 'viewer', label: 'Viewer' },
      { value: ROLES.ADMIN, label: 'Admin' },
      { value: ROLES.SUPERADMIN, label: 'Superadmin' },
    ];

    if (effectiveEditorRole === ROLES.SUPERADMIN) {
      return base;
    }

    if (effectiveEditorRole === ROLES.ADMIN) {
      return base.filter(option => option.value !== ROLES.SUPERADMIN);
    }

    if (effectiveEditorRole === ROLES.TENANT_ADMIN) {
      const allowed = new Set([
        ROLES.TENANT_ADMIN,
        'site_admin',
        'operator',
        'viewer',
      ]);
      return base.filter(option => allowed.has(option.value));
    }

    // Default: only operator and viewer
    const allowed = new Set(['operator', 'viewer']);
    return base.filter(option => allowed.has(option.value));
  }, [effectiveEditorRole]);

  // Tenant context for resolving tenant names
  const { availableTenants } = useTenant();

  const tenantName = React.useMemo(
    () => tenantDisplayName(availableTenants, formData?.tenantId, defaultTenantName),
    [formData?.tenantId, availableTenants, defaultTenantName]
  );

  const siteTenantId = formData?.tenantId || defaultTenantId || dealerAdmin?.tenantId || userClaims?.tenantId || userData?.tenantId;

  useEffect(() => {
    const userId = user?.uid || user?.id;
    if (!isOpen || !siteTenantId || !userId) {
      if (isNew) setAlgoDeviceSupport(false);
      return undefined;
    }
    let live = true;
    getTenantMemberships(siteTenantId, { includeHidden: true })
      .then((rows) => {
        if (!live) return;
        const list = Array.isArray(rows) ? rows : [];
        const match = list.find((row) => row.userId === userId);
        setAlgoDeviceSupport(match?.algoDeviceSupport === true);
      })
      .catch(() => {
        if (live) setAlgoDeviceSupport(false);
      });
    return () => {
      live = false;
    };
  }, [isOpen, isNew, siteTenantId, user?.uid, user?.id]);

  const handleSiteCreated = (message, created) => {
    toast.success(message);
    setSiteEditorOpen(false);
    setError(null);
    const createdId = created?.id || created?.siteId;
    if (createdId) {
      setSites((prev) => {
        if (prev.some((site) => site.id === createdId)) return prev;
        return [...prev, { ...created, id: createdId }].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      });
      setFormData((prev) => ({ ...prev, siteId: createdId }));
    }
  };

  // Reset form when user changes or modal opens
  useEffect(() => {
    // Merge user data with initial state, preserving nested objects
    const mergedData = {
      ...initialState,
      tenantId: user?.tenantId || defaultTenantId || initialState.tenantId,
      ...(user || {}),
      role: isNew ? ROLES.TENANT_ADMIN : (user?.role || ROLES.TENANT_ADMIN),
      // Ensure permissions object exists with defaults
      permissions: {
        ...initialState.permissions,
        ...(user?.permissions || {}),
      },
      requiredAgreements: {
        ...initialState.requiredAgreements,
        ...(user?.requiredAgreements || {}),
      },
      // Ensure claims objects exist with defaults
      claims: {
        ...initialState.claims,
        ...(user?.claims || {}),
      },
      customClaims: {
        ...initialState.customClaims,
        ...(user?.customClaims || {}),
      },
    };
    if (isNew) {
      mergedData.claims = { ...mergedData.claims, role: ROLES.TENANT_ADMIN };
      mergedData.customClaims = { ...mergedData.customClaims, role: ROLES.TENANT_ADMIN };
    }

    setFormData(mergedData);
    setError(null);
    setInviteLink(null);
    setInviteStatus(null);
    setCreatedUserId(null);
    setSiteEditorOpen(false);
  }, [user, isOpen, isNew, defaultTenantId]);

  // Fetch sites for the current tenant
  useEffect(() => {
    if (!isOpen) return;

    const tenantId = formData?.tenantId || defaultTenantId || dealerAdmin?.tenantId || userClaims?.tenantId || userData?.tenantId;
    if (!tenantId) {
      console.log('[UserFormModal] No tenantId available for fetching sites');
      return;
    }

    console.log('[UserFormModal] Fetching sites for tenant:', tenantId);
    if (readSupportedTenantSession()?.tenantId === tenantId) {
      if (!currentUser?.uid) return undefined;
      let cancelled = false;
      getSites(tenantId).then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        const sitesData = rows.map((site) => ({
          ...site,
          id: site?.id || site?.siteId,
        })).filter((site) => site.id);
        setSites(sitesData.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      }).catch((error) => {
        console.error('[UserFormModal] Error fetching sites:', error);
      });
      return () => {
        cancelled = true;
      };
    }
    const db = getFirestore();
    const sitesRef = collection(db, 'tenants', tenantId, 'sites');
    const sitesQuery = query(sitesRef);

    const unsubscribe = onSnapshot(sitesQuery, (snapshot) => {
      const sitesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log('[UserFormModal] Sites loaded:', sitesData.length, sitesData);
      setSites(sitesData.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    }, (error) => {
      console.error('[UserFormModal] Error fetching sites:', error);
    });

    return () => unsubscribe();
  }, [isOpen, currentUser?.uid, formData?.tenantId, defaultTenantId, dealerAdmin?.tenantId, userClaims, userData]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const tenantId = formData?.tenantId || defaultTenantId || dealerAdmin?.tenantId || userClaims?.tenantId || userData?.tenantId;
    if (!tenantId) {
      setTenantPstn({ loading: false, enabled: false, callerIdFrom: '' });
      return undefined;
    }

    let cancelled = false;
    setTenantPstn((prev) => ({ ...prev, loading: true }));
    (async () => {
      try {
        const resp = await fetchWithAuth(`/api/v2/tenants/${tenantId}/sip-config`);
        const data = resp?.data || {};
        if (!cancelled) {
          setTenantPstn({
            loading: false,
            enabled: Boolean(data.enableOutboundPstn),
            callerIdFrom: data.callerIdFrom || '',
          });
        }
      } catch {
        if (!cancelled) {
          setTenantPstn({ loading: false, enabled: false, callerIdFrom: '' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData?.tenantId, defaultTenantId, dealerAdmin?.tenantId, userClaims, userData]);

  const effectiveUid = formData.uid || createdUserId;
  const showInvitation = Boolean(effectiveUid);

  useEffect(() => {
    if (!isOpen || !effectiveUid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const endpoint = API_ROUTES.USERS_INVITE_STATUS.replace(':uid', encodeURIComponent(effectiveUid));
        const resp = await authenticatedFetch(endpoint);
        const json = await resp.json();
        if (!cancelled && resp.ok) {
          setInviteStatus(json.data || json);
        }
      } catch (err) {
        console.warn('[UserFormModal] Could not load invite status:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, effectiveUid]);

  const handleInvite = async (delivery) => {
    if (!formData.email) {
      toast.error('Email is required before inviting');
      return;
    }
    setInviteBusy(true);
    setError(null);
    try {
      const resp = await authenticatedFetch(API_ROUTES.USERS_SEND_INVITE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, delivery }),
      });
      const json = await resp.json();
      if (json.inviteStatus) setInviteStatus(json.inviteStatus);
      if (json.inviteLink) setInviteLink(json.inviteLink);

      if (json.code === 'email_failed' && json.inviteLink) {
        try {
          await navigator.clipboard.writeText(json.inviteLink);
        } catch {
          /* clipboard is optional; link remains visible */
        }
        const message = json.error || 'Invitation email could not be sent. Copy the link below.';
        setError(message);
        toast.error(message);
        return;
      }

      if (!resp.ok) {
        throw new Error(json.error || 'Could not create invite');
      }

      if (delivery === 'copy' && json.inviteLink) {
        try {
          await navigator.clipboard.writeText(json.inviteLink);
          toast.success('Invite link copied. Sending or copying again replaces any unused invite.');
        } catch {
          toast.success('Invite created. Copy the link below.');
        }
      } else if (delivery === 'email') {
        toast.success('Invitation email sent.');
      }
    } catch (err) {
      setError(err.message || 'Could not create invite');
      toast.error(err.message || 'Could not create invite');
    } finally {
      setInviteBusy(false);
    }
  };

  // Handle changes to form fields, including nested objects
  const handleChange = e => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    const fieldValue = type === 'checkbox' ? checked : value;

    // Handle nested fields (using dot notation in name attribute)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: fieldValue,
        },
      }));
    } else {
      // Handle top-level fields
      setFormData(prev => ({
        ...prev,
        [name]: fieldValue,
      }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate required fields
      if (isNew && !formData.email) {
        throw new Error('Email is required');
      }

      if (!formData.role) {
        throw new Error('Role is required');
      }

      // Validate siteId required unless tenant_admin
      if (formData.role !== 'tenant_admin' && formData.role !== 'admin' && formData.role !== 'superadmin' && !formData.siteId) {
        throw new Error('Site is required for this role');
      }

      // Prepare the payload based on whether we're creating or updating
      let payload;

      if (isNew) {
        // For new users, do NOT send uid
        const { uid, ...rest } = formData;
        payload = {
          email: rest.email,
          role: rest.role,
          tenantId: rest.tenantId,
          permissions: rest.permissions,
          requiredAgreements: {
            nda: !!rest.requiredAgreements?.nda,
            beta: !!rest.requiredAgreements?.beta,
          },
        };
        // Always add siteId if it's set
        if (rest.siteId) {
          payload.siteId = rest.siteId;
        }
      } else {
        // For existing users, only include editable fields
        payload = {
          uid: formData.uid, // Required for identifying the user
          role: formData.role,
          permissions: formData.permissions,
          requiredAgreements: {
            nda: !!formData.requiredAgreements?.nda,
            beta: !!formData.requiredAgreements?.beta,
          },
        };
        // Always add siteId if it's set (even for tenant-wide roles)
        if (formData.siteId) {
          payload.siteId = formData.siteId;
        }
      }

      console.log(`📤 ${isNew ? 'Creating' : 'Updating'} user with payload:`, payload);

      // Validate uid for updates
      if (!isNew && !formData.uid) {
        throw new Error('User ID (uid) is required to update a user');
      }

      // Determine the endpoint based on operation (canonical API paths)
      const endpoint = isNew
        ? '/api/users/create'
        : `/api/users/update/${formData.uid}`;
      const method = isNew ? 'POST' : 'PATCH';

      // Make the API call
      const response = await authenticatedFetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || errorData.error || `Failed to ${isNew ? 'create' : 'update'} user`
        );
      }

      // Success
      const responseData = await response.json();
      const savedUserId = responseData.userId || responseData.uid || formData.uid;
      if (canAssignAlgo && savedUserId && siteTenantId) {
        await updateTenantMembershipAdmin(siteTenantId, savedUserId, {
          algoDeviceSupport: algoDeviceSupport === true,
        });
      }

      if (isNew) {
        const newUid = responseData.userId || responseData.uid;
        if (newUid) {
          setCreatedUserId(newUid);
          setFormData(prev => ({ ...prev, uid: newUid }));
        }
        toast.success('User created. Send or copy an invite below.');
        onSave(responseData);
        return;
      }

      toast.success('User updated successfully!');
      onSave(responseData);
      onClose();
    } catch (err) {
      console.error(`Error ${isNew ? 'creating' : 'updating'} user:`, err);
      setError(err.message || `Failed to ${isNew ? 'create' : 'update'} user`);
      toast.error(err.message || `Failed to ${isNew ? 'create' : 'update'} user`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Local helper to format various timestamp shapes
  const formatTimestamp = timestamp => {
    if (!timestamp) return '';
    try {
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      if (typeof timestamp === 'string') {
        const d = new Date(timestamp);
        return isNaN(d.getTime()) ? timestamp : d.toLocaleString();
      }
      const d = new Date(timestamp);
      return isNaN(d.getTime()) ? '' : d.toLocaleString();
    } catch {
      return '';
    }
  };

  return (
    <div className="fixed inset-0  bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              {isNew ? 'Add New User' : 'Edit User'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">{error}</div>}

          <form onSubmit={handleSubmit} className="ca-form">
            <div className="flex items-center gap-3">
              {!(isNew && !createdUserId) && (
                formData.photoURL ? (
                  <img
                    src={formData.photoURL}
                    alt={formData.displayName || formData.email || 'User'}
                    className="h-10 w-10 rounded-full object-cover border shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 shrink-0">
                    <span className="text-sm font-semibold">
                      {(formData.displayName || formData.email || '?').slice(0, 1).toUpperCase()}
                    </span>
                  </div>
                )
              )}
              <div className="min-w-0 flex-1">
                {isNew && !createdUserId ? (
                  <div className="ca-form-grid">
                    <div className="ca-field">
                      <label htmlFor="email" className="ca-label">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleChange}
                        className="ca-input"
                        placeholder="user@example.com"
                        required
                      />
                    </div>
                    <div className="ca-field">
                      <label htmlFor="tenantName" className="ca-label">Tenant</label>
                      <input
                        type="text"
                        id="tenantName"
                        name="tenantName"
                        value={tenantName}
                        readOnly
                        className="ca-input bg-gray-50 text-gray-800"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {formData.displayName || formData.email || '—'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {[
                        formData.displayName ? formData.email : null,
                        tenantName,
                        formatTimestamp(formData.createdAt),
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-sm font-medium text-blue-700 mb-3">Role & Permissions</h3>

              <div className="ca-form-grid">
                <div className="ca-field">
                  <label htmlFor="role" className="ca-label">Role</label>
                  {formData.role === ROLES.SUPERADMIN && effectiveEditorRole !== ROLES.SUPERADMIN ? (
                    <div className="text-sm font-medium text-gray-900">Superadmin</div>
                  ) : (
                    <select
                      id="role"
                      name="role"
                      value={formData.role || ROLES.TENANT_ADMIN}
                      onChange={handleChange}
                      className="ca-select"
                      required
                    >
                      {roleOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {formData.role && (
                  <div className="ca-field">
                    <label htmlFor="siteId" className="ca-label">
                      Site {formData.role !== 'tenant_admin' && formData.role !== 'admin' && formData.role !== 'superadmin' && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex items-start gap-2">
                      <select
                        id="siteId"
                        name="siteId"
                        value={formData.siteId || ''}
                        onChange={handleChange}
                        className="ca-select flex-1"
                        required={formData.role !== 'tenant_admin' && formData.role !== 'admin' && formData.role !== 'superadmin'}
                        disabled={sites.length === 0}
                      >
                        <option value="">
                          {sites.length === 0 ? 'No sites available' : 'Select a site…'}
                        </option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name} {site.isDefault ? '(Default)' : ''}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setSiteEditorOpen(true)}
                        disabled={!siteTenantId}
                      >
                        Add Site
                      </Button>
                    </div>
                    {sites.length === 0 && (
                      <p className="ca-help text-orange-600">Add a site here, then save this user.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Permissions</h4>
                {canAssignAlgo ? (
                <label htmlFor="algoDeviceSupport" className="inline-flex items-start mb-3">
                  <input
                    type="checkbox"
                    id="algoDeviceSupport"
                    checked={algoDeviceSupport === true}
                    onChange={(event) => setAlgoDeviceSupport(event.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2">
                    <span className="text-sm text-gray-700">{ALGO_DEVICE_SUPPORT_LABEL}</span>
                    <span className="block text-xs text-gray-500">{ALGO_DEVICE_SUPPORT_HELP}</span>
                  </span>
                </label>
                ) : null}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <label htmlFor="permissions.canConfigure" className="inline-flex items-center">
                    <input
                      type="checkbox"
                      id="permissions.canConfigure"
                      name="permissions.canConfigure"
                      checked={formData.permissions?.canConfigure || false}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Can Configure</span>
                  </label>

                  <label htmlFor="permissions.canTriggerEvents" className="inline-flex items-center">
                    <input
                      type="checkbox"
                      id="permissions.canTriggerEvents"
                      name="permissions.canTriggerEvents"
                      checked={formData.permissions?.canTriggerEvents || false}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Can Trigger Events</span>
                  </label>

                  <label htmlFor="permissions.receivesNotifications" className="inline-flex items-center">
                    <input
                      type="checkbox"
                      id="permissions.receivesNotifications"
                      name="permissions.receivesNotifications"
                      checked={formData.permissions?.receivesNotifications || false}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Receives Notifications</span>
                  </label>

                  {canAssignPricing(assignerRole) ? (
                    <label htmlFor="permissions.pricingManagement" className="inline-flex items-center">
                      <input
                        type="checkbox"
                        id="permissions.pricingManagement"
                        name="permissions.pricingManagement"
                        checked={formData.role === 'superadmin' || formData.permissions?.pricingManagement === true}
                        onChange={handleChange}
                        disabled={formData.role === 'superadmin'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Pricing Management</span>
                    </label>
                  ) : null}

                  <div className="w-full">
                    <label htmlFor="permissions.phoneEnabled" className="inline-flex items-center">
                      <input
                        type="checkbox"
                        id="permissions.phoneEnabled"
                        name="permissions.phoneEnabled"
                        checked={formData.permissions?.phoneEnabled || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Enable browser phone</span>
                    </label>
                    {formData.permissions?.phoneEnabled && !tenantPstn.loading && !tenantPstn.enabled && (
                      <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        Tenant outbound PSTN is off. This user can get the browser phone, but calls to regular phone numbers will fail until you enable Outbound PSTN under Tenant Management → PSTN → Outbound.
                      </p>
                    )}
                    {formData.permissions?.phoneEnabled && tenantPstn.enabled && !tenantPstn.callerIdFrom && (
                      <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        Outbound PSTN is on, but no caller ID is selected. Choose a number on Tenant Management → PSTN → Outbound.
                      </p>
                    )}
                  </div>

                  <label htmlFor="permissions.remindersEnabled" className="inline-flex items-center">
                    <input
                      type="checkbox"
                      id="permissions.remindersEnabled"
                      name="permissions.remindersEnabled"
                      checked={formData.permissions?.remindersEnabled || false}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enable reminders and timers</span>
                  </label>

                  <label htmlFor="permissions.emailRemEnabled" className="inline-flex items-center">
                    <input
                      type="checkbox"
                      id="permissions.emailRemEnabled"
                      name="permissions.emailRemEnabled"
                      checked={formData.permissions?.emailRemEnabled || false}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enable Email Reminders</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-md">
              <h3 className="text-sm font-medium text-gray-800 mb-3">Required Agreements</h3>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <label htmlFor="requiredAgreements.nda" className="inline-flex items-center">
                  <input
                    type="checkbox"
                    id="requiredAgreements.nda"
                    name="requiredAgreements.nda"
                    checked={formData.requiredAgreements?.nda || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Require NDA</span>
                </label>
                <label htmlFor="requiredAgreements.beta" className="inline-flex items-center">
                  <input
                    type="checkbox"
                    id="requiredAgreements.beta"
                    name="requiredAgreements.beta"
                    checked={formData.requiredAgreements?.beta || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Require Beta Agreement</span>
                </label>
              </div>
            </div>

            {showInvitation && (
              <div className="bg-white border border-gray-200 p-4 rounded-md space-y-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-800 mb-1">Invitation</h3>
                  <div className="text-sm text-gray-700 mb-3 space-y-0.5">
                    <div>
                      Status:{' '}
                      <span className="font-medium">{formatInviteStatus(inviteStatus?.status)}</span>
                    </div>
                    {inviteStatus?.status === 'pending' && inviteStatus?.expiresAt && (
                      <div>Expires: {formatInviteDate(inviteStatus.expiresAt)}</div>
                    )}
                  </div>
                  {inviteStatus?.status !== 'accepted' && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        onClick={() => handleInvite('email')}
                        disabled={inviteBusy || !formData.email}
                      >
                        {inviteBusy ? 'Working...' : 'Send Invite'}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => handleInvite('copy')}
                        disabled={inviteBusy || !formData.email}
                      >
                        Copy Invite Link
                      </Button>
                    </div>
                  )}
                </div>
                {inviteLink && inviteStatus?.status !== 'accepted' && (
                  <input
                    type="text"
                    readOnly
                    value={inviteLink}
                    className="w-full text-xs p-2 border border-gray-300 rounded bg-gray-50 font-mono"
                    onClick={e => e.target.select()}
                  />
                )}
              </div>
            )}

            <div className="ca-actions">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting || inviteBusy}>
                {createdUserId ? 'Done' : 'Cancel'}
              </Button>
              {!createdUserId && (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : isNew ? 'Create User' : 'Save Changes'}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
      {siteEditorOpen && (
        <SiteEditorDialog
          open={siteEditorOpen}
          site={null}
          tenantId={siteTenantId}
          onClose={() => setSiteEditorOpen(false)}
          onSuccess={handleSiteCreated}
          onError={(message) => toast.error(message)}
        />
      )}
    </div>
  );
};

export default UserFormModal;
